// Shared canonical topic-key order/labels for the Detail view's topic set —
// used by DetailView.js (building its breakdown rows) and
// ConversationsResultsV3.js (building "What Changed Since Last Run" rows,
// now rendered beside the trend chart rather than inside DetailView) so the
// two don't carry separate copies of the same mapping.
//
// Response-key order for the default topic set (matches
// RESPONSE_KEY_TO_TOPIC_KEY on the backend, minus purchaseJourneyQuestions —
// excluded from sentiment scoring and from this item-level view).
//
// purchaseBarriers/purchaseEnablers and customerComplaints/customerPraises are
// intentionally absent — same grouping TrendsView.js already shows as one
// combined line each (purchaseEnablersBarriers / customerComplaintsPraises).
// The backend (getConversationMonitoringDetail) additively attaches these two
// synthetic combined-score entries to the response alongside the original 4
// topic keys — see TOPIC_GROUPS below, which maps each combined key back to
// its two real source topics for DetailView's expandable "2 subsections" item
// table.
export const DEFAULT_TOPIC_ORDER = [
  "discussionTopics",
  "keyQuestions",
  "purchaseEnablersBarriers",
  "customerComplaintsPraises",
  "customerNeeds",
  "customerPainPoints",
  "customerEmergingTrends",
];

export const DEFAULT_TOPIC_LABELS = {
  discussionTopics: "Discussion Topics",
  purchaseEnablersBarriers: "Purchase Enablers vs Barriers",
  keyQuestions: "Key Questions",
  customerNeeds: "Customer Needs",
  customerPainPoints: "Customer Pain Points",
  customerComplaintsPraises: "Customer Complaints vs Praises",
  customerEmergingTrends: "Emerging Trends",
};

// Combined-key -> its two real source topics, for building the expandable
// row's two labeled subsections (each rendered via its own TopicItemTable).
export const TOPIC_GROUPS = {
  purchaseEnablersBarriers: [
    { key: "purchaseEnablers", label: "Purchase Enablers" },
    { key: "purchaseBarriers", label: "Purchase Barriers" },
  ],
  customerComplaintsPraises: [
    { key: "customerComplaints", label: "Customer Complaints" },
    { key: "customerPraises", label: "Customer Praises" },
  ],
};

export function humanizeTopicKey(key) {
  if (DEFAULT_TOPIC_LABELS[key]) return DEFAULT_TOPIC_LABELS[key];
  // Dynamic (per-customer) topic keys look like "EmployerKeyDiscussionTopicsV1"
  return key
    .replace(/V\d+$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

// Fixed colors for the default topics — pinned to the topic name, not its
// position in the array the backend returns, so e.g. "Purchase Barriers" is
// always red regardless of what other topics a given customer has configured
// alongside it (position would shift that color run to run). Shared so the
// trend chart's lines and WhatChangedSection's topic dots are always the
// same color for the same topic — originally lived only in TrendsView.js.
//
// dataviz skill's validated categorical palette (8 slots, fixed adjacent-safe
// order: blue/orange/aqua/yellow/magenta/green/violet/red — run through
// validate_palette.js, passes the adjacent-pair CVD/contrast gates). Assigned
// here in the same order TrendsView.js plots lines in, so two topics that are
// visual neighbors on the chart are never visual neighbors in the palette too
// (the old scheme put Key Questions' orange right next to Customer
// Complaints/Praises' near-identical yellow — this order separates them with
// aqua in between).
export const DEFAULT_TOPIC_COLORS = {
  discussionTopics: "#2a78d6",
  keyQuestions: "#eb6834",
  purchaseEnablersBarriers: "#1baf7a",
  customerComplaintsPraises: "#eda100",
  customerNeeds: "#e87ba4",
  customerEmergingTrends: "#008300",
  customerPainPoints: "#4a3aa7",
};

const TOPIC_COLOR_PALETTE = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100",
  "#e87ba4", "#008300", "#4a3aa7", "#e34948",
];

// djb2 string hash — deterministic, so a dynamic/per-customer topic key not
// in DEFAULT_TOPIC_COLORS always lands on the same palette slot too, rather
// than one derived from its position in that particular fetch's response.
function hashTopicKey(key) {
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 33) ^ key.charCodeAt(i);
  }
  return Math.abs(hash);
}

export function getTopicColor(topicKey) {
  if (DEFAULT_TOPIC_COLORS[topicKey]) return DEFAULT_TOPIC_COLORS[topicKey];
  return TOPIC_COLOR_PALETTE[hashTopicKey(topicKey) % TOPIC_COLOR_PALETTE.length];
}
